import JSZip from "jszip";

// Mission Ready data packages (.aeropack): a ZIP with info.json, manifest.json (per-file SHA-256) and collections/*.jsonl.
export const TYPE_NAMES = { seed: "Seed", config: "Config", deployment: "Deployment", return: "Return" };

const hex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
const sha256 = async (bytes) => hex(await crypto.subtle.digest("SHA-256", bytes));

export const readAeropack = async (file) => {
  const zip = await JSZip.loadAsync(file);
  const infoFile = zip.file("info.json");
  if (!infoFile) return null;
  const infoBytes = await infoFile.async("uint8array");
  const info = JSON.parse(new TextDecoder().decode(infoBytes));
  if (info?.kind !== "missionready-data-bundle") return null;
  const manifestFile = zip.file("manifest.json");
  const manifest = manifestFile ? JSON.parse(await manifestFile.async("string")) : null;

  // Checks info.json and every collection file against the checksums in manifest.json.
  const verify = async (onProgress = () => {}) => {
    if (!manifest) return { ok: false, total: 0, bad: [], missing: ["manifest.json"], infoOk: false };
    const infoOk = !manifest.info_sha256 || (await sha256(infoBytes)) === manifest.info_sha256;
    const cols = Array.isArray(manifest.collections) ? manifest.collections : [];
    const bad = [];
    const missing = [];
    for (let i = 0; i < cols.length; i += 1) {
      const c = cols[i];
      const entry = zip.file(`collections/${c.name}.jsonl`);
      if (!entry) missing.push(c.name);
      else if (c.sha256 && (await sha256(await entry.async("uint8array"))) !== c.sha256) bad.push(c.name);
      onProgress(i + 1, cols.length);
    }
    return { ok: infoOk && !bad.length && !missing.length, total: cols.length, bad, missing, infoOk };
  };

  return { info, manifest, verify };
};

const dateOf = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const destinationText = (info) => {
  const d = info?.destination ?? {};
  if (d.kind === "unit") return d.name || "One unit";
  if (d.kind === "all-units") return "All units";
  if (d.kind === "base") return "Back to base";
  return d.kind || "Not stated";
};

// The details the upload form fills in automatically from info.json.
export const aeropackMeta = (info) => {
  const type = TYPE_NAMES[info.package_type] ?? "Data package";
  const label = info.package_label || type;
  const made = dateOf(info.created_at);
  const parts = [`${label} for ${destinationText(info)}.`];
  if (info.created_by) parts.push(`Made by ${info.created_by}${made ? ` on ${made}` : ""}.`);
  if (info.min_build != null) parts.push(`Needs ${info.app || "the app"} build ${info.min_build} or later.`);
  return {
    fileType: type,
    version: info.app_build != null ? `${info.app_build}${info.app_stage ? ` ${info.app_stage}` : ""}` : "",
    fileDate: made,
    description: parts.join(" "),
  };
};

export const aeropackInfoFields = (info) => ({
  label: info.package_label || "",
  destination: destinationText(info),
  destination_id: info.destination?.id || "",
  app: info.app || "",
  app_build: info.app_build != null ? String(info.app_build) : "",
  app_stage: info.app_stage || "",
  created_by: info.created_by || "",
  created_at: info.created_at || "",
  format_version: info.format_version != null ? String(info.format_version) : "",
});
